import { InvestorAccountModel } from '@common/models/InvestorAccountModel';
import { RewardsModel } from '@common/models/RewardsModel';
import { TradesModel } from '@common/models/TradesModel';
import { TransactionsModel } from '@common/models/TransactionsModel';
import { TradeType } from '@services/appsync/types';
import create from 'zustand';
import { REWARDS_FILTER, TRADES_FILTER, TRANSACTION_FILTER } from '../config';
import { ActivityRecord } from '../types';

interface ActivityViewModelProps {
  isLoading: boolean;
  records: ActivityRecord[];
  sortByDateDir: 'asc' | 'desc';
}
interface ActivityViewModelActions {
  fetchActivity: () => Promise<{ error?: string; success: boolean }>;
  sortByDate: (sort: 'asc' | 'desc') => void;
}

export const useActivityViewModel = create<ActivityViewModelProps & ActivityViewModelActions>((set, get) => ({
  fetchActivity: async () => {
    const { investorAccount } = InvestorAccountModel.getState();
    if (!investorAccount) return { error: 'No investor account', success: false };

    const records: ActivityRecord[] = [];

    // Get Transactions
    const transactions = (await TransactionsModel.getState().fetchTransactions(investorAccount)) ?? [];
    transactions
      .filter((t) => t.code && TRANSACTION_FILTER.includes(t.code))
      .forEach((t) =>
        records.push({
          dataType: t,
          date: t.posted_at,
          subtype: t.code,
          title: t.title ?? null,
          type: `Transaction`,
        }),
      );

    // Get Trades
    const trades = (await TradesModel.getState().fetchTrades(investorAccount)) ?? [];
    trades
      .filter((t) => t.status && TRADES_FILTER.includes(t.status))
      .forEach((t) =>
        records.push({
          dataType: t,
          date: t.type === TradeType.PURCHASE ? t.created_at : t.settled_at,
          subtype: t.type,
          title: t.type ?? null,
          type: `Trade`,
        }),
      );

    // Get Rewards
    const rewards = (await RewardsModel.getState().fetchRewards(investorAccount)) ?? [];
    rewards
      .filter((r) => r.code && REWARDS_FILTER.includes(r.code))
      .forEach((r) =>
        records.push({
          dataType: r,
          date: r.posted_at,
          subtype: r.code,
          title: r.code ?? null,
          type: `Reward`,
        }),
      );

    const sortedRecords = records.sort((a, b) => {
      const dateA = new Date(a.date as string);
      const dateB = new Date(b.date as string);
      return dateB.getTime() - dateA.getTime();
    });

    set({
      isLoading: false,
      records: sortedRecords,
    });
    return { success: true };
  },
  isLoading: true,
  records: [],
  sortByDate: (direction) => {
    const dateSortedRecords = get().records.sort((a, b) => {
      const dateA = new Date(a.date as string);
      const dateB = new Date(b.date as string);
      if (direction === 'asc') {
        return dateA.getTime() - dateB.getTime();
      } else {
        return dateB.getTime() - dateA.getTime();
      }
    });
    set({
      records: dateSortedRecords,
      sortByDateDir: direction,
    });
  },
  sortByDateDir: 'desc',
}));
